// --- React & Redux
import React from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Row, Col } from 'antd';
import Input from '@dtci/spellbook/Input';
import { MultiSelect } from '@dtci/spellbook/MultiSelect';
import Select from '@dtci/spellbook/Select';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Date Library
import moment from 'moment-timezone';
// --- Store & Hooks
import { useUsersActions } from 'datastage/store/users/useUsers';
// --- Utils
import { getFormValue, getSelectFormValue } from '../../../utils/userHelpers';

/**
 * Form Section Component
 * Renders a titled section with content.
 */
const FormSection = ({ title, children }) => (
    <div className="form-section">
        <h4 className="form-section-title">{title}</h4>
        <div className="form-section-content">{children}</div>
    </div>
);

/**
 * Validation Error Component
 * Displays a validation error message if present.
 */
const ValidationError = ({ error }) => {
    if (!error) return null;
    return <div className="validation-error">{error}</div>;
};

/**
 * User Form Component (Unified Create/Edit)
 *
 * Features:
 * - Renders a form for creating or editing user details
 * - Shows different fields based on mode (create vs edit)
 * - Pre-fills form fields with selected user data in edit mode
 * - Updates form state on input changes
 * - Displays validation errors for required fields
 *
 * @param {Object} props
 * @param {boolean} props.isCreate - Whether the form is in create mode
 * @param {Object} props.validationErrors - Object containing field validation errors
 * @returns {JSX.Element}
 */
export function UserForm({ isCreate, validationErrors = {} }) {
    const { updateUserForm } = useUsersActions();
    const { statusOptions, roleOptions, userForm, selectedUser } = useSelector(
        (state) => state.users,
    );

    const handleInputChange = (field, value) => {
        updateUserForm({
            key: 'userForm',
            data: { [field]: value },
        });
    };

    /**
     * Common field: Username
     */
    const renderUsernameField = (colSpan = 24) => (
        <InputLabel colProps={{ span: colSpan }} label="Username" required={isCreate}>
            <Input
                value={
                    isCreate
                        ? userForm?.username
                        : getFormValue(userForm?.username, selectedUser?.username || '')
                }
                width="auto"
                id="username"
                name="username"
                onChange={(e) => handleInputChange('username', e.target.value)}
                className={validationErrors.username ? 'input-error' : ''}
            />
            <ValidationError error={validationErrors.username} />
        </InputLabel>
    );

    /**
     * Common field: Roles
     */
    const renderRolesField = () => (
        <InputLabel colProps={{ span: 24 }} label="Roles" required={isCreate}>
            <MultiSelect
                onChange={(value) => handleInputChange('spaUserRoleIds', value)}
                name="spaUserRoleIds"
                options={roleOptions}
                isSearchable
                isClearable
                value={
                    isCreate
                        ? userForm?.spaUserRoleIds
                        : getFormValue(
                              userForm?.spaUserRoleIds,
                              roleOptions.filter((item) =>
                                  (selectedUser?.spaUserRoleIds ?? []).includes(item.value),
                              ) || '',
                          )
                }
                className={validationErrors.spaUserRoleIds ? 'input-error' : ''}
            />
            <ValidationError error={validationErrors.spaUserRoleIds} />
        </InputLabel>
    );

    // Create mode: Centered form with helper text
    if (isCreate) {
        return (
            <div className="create-mode-form">
                <p className="form-helper-text">
                    Enter a username and assign roles to create a new user.
                </p>
                <Row>
                    {renderUsernameField()}
                    {renderRolesField()}
                </Row>
            </div>
        );
    }

    // Edit mode: Grouped fields with sections
    return (
        <div className="edit-mode-form">
            {/* User Information Section - Read-only metadata */}
            <FormSection title="User Information">
                <Row gutter={16} className="metadata-row">
                    <Col span={12}>
                        <InputLabel colProps={{ span: 24 }} label="ID">
                            <div className="disabled-field">
                                <Input
                                    value={selectedUser?.id}
                                    width="auto"
                                    id="id"
                                    name="id"
                                    isDisabled
                                />
                            </div>
                        </InputLabel>
                    </Col>
                    <Col span={12}>
                        <InputLabel colProps={{ span: 24 }} label="Created At">
                            <div className="disabled-field">
                                <Input
                                    value={
                                        selectedUser?.createdAt
                                            ? moment(selectedUser.createdAt).format('MM/DD/YYYY')
                                            : ''
                                    }
                                    width="auto"
                                    id="createdAt"
                                    name="createdAt"
                                    isDisabled
                                />
                            </div>
                        </InputLabel>
                    </Col>
                    <Col span={12}>
                        <InputLabel colProps={{ span: 24 }} label="Modified At">
                            <div className="disabled-field">
                                <Input
                                    value={
                                        selectedUser?.modifiedAt
                                            ? moment(selectedUser.modifiedAt).format('MM/DD/YYYY')
                                            : ''
                                    }
                                    width="auto"
                                    id="modifiedAt"
                                    name="modifiedAt"
                                    isDisabled
                                />
                            </div>
                        </InputLabel>
                    </Col>
                    {selectedUser?.lastLogin && (
                        <Col span={12}>
                            <InputLabel colProps={{ span: 24 }} label="Last Login">
                                <div className="disabled-field">
                                    <Input
                                        value={moment.utc(selectedUser.lastLogin).tz(moment.tz.guess()).format('MM/DD/YYYY, h:mm:ss A z')}
                                        width="auto"
                                        id="lastLogin"
                                        name="lastLogin"
                                        isDisabled
                                    />
                                </div>
                            </InputLabel>
                        </Col>
                    )}
                </Row>
            </FormSection>

            {/* Account Details Section - Editable fields */}
            <FormSection title="Account Details">
                <Row gutter={16}>
                    <Col span={24}>{renderUsernameField()}</Col>
                    <Col span={24}>
                        <InputLabel colProps={{ span: 24 }} label="Full Name">
                            <Input
                                value={getFormValue(
                                    userForm?.fullName,
                                    selectedUser?.fullName || '',
                                )}
                                width="auto"
                                id="fullName"
                                name="fullName"
                                onChange={(e) => handleInputChange('fullName', e.target.value)}
                            />
                        </InputLabel>
                    </Col>
                    <Col span={12}>
                        <InputLabel colProps={{ span: 24 }} label="Status">
                            <Select
                                options={statusOptions}
                                isClearable
                                isSearchable
                                onChange={(value) => handleInputChange('status', value)}
                                value={getSelectFormValue(
                                    userForm?.status,
                                    selectedUser?.status,
                                    statusOptions,
                                )}
                            />
                        </InputLabel>
                    </Col>
                    <Col span={12}>
                        <InputLabel colProps={{ span: 24 }} label="Rightsline ID">
                            <Input
                                value={getFormValue(
                                    userForm?.rightslineId,
                                    selectedUser?.rightslineId || '',
                                )}
                                width="auto"
                                id="rightslineId"
                                name="rightslineId"
                                onChange={(e) => handleInputChange('rightslineId', e.target.value)}
                            />
                        </InputLabel>
                    </Col>
                </Row>
            </FormSection>

            {/* Permissions Section */}
            <FormSection title="Permissions">
                <Row>{renderRolesField()}</Row>
            </FormSection>
        </div>
    );
}
